<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import ChevronIcon from '@/components/base/assets/ChevronIcon.vue';
import Modal from '@/components/base/layout/Modal.vue';
import RouteLink from '@/components/base/RouteLink.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Caption from '@/components/base/typography/Caption.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import Header5 from '@/components/base/typography/Header5.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { useBundlePromo } from '@/stores/bundlePromo';
import { sendModalViewed } from '@/utils/analytics/modalViewed';

const { productKey } = defineProps<{
  productKey: string;
}>();

const isModalOpen = ref(false);

const { promosByProductKey } = storeToRefs(useBundlePromo());

const promoAttached = computed(() => promosByProductKey.value.get(productKey)?.[0]);

const handleOpenModal = () => {
  isModalOpen.value = true;
  sendModalViewed({
    modalName: 'Promotion Details Modal',
    productId: productKey,
  });
};
</script>

<template>
  <div v-if="promoAttached">
    <UnstyledButton @click="handleOpenModal">
      <Caption
        class="flex gap-1 items-center py-0.5 pl-1.5 font-semibold text-black rounded-md cursor-pointer w-fit bg-nuts-amber-400"
      >
        {{ promoAttached.cartDiscount.name.en }}
        <ChevronIcon direction="right" :size="16" />
      </Caption>
    </UnstyledButton>
    <Teleport to="#teleported">
      <Modal
        aria-label="Bundle Discount Details"
        anchor="right"
        :hasControls="false"
        :isOpen="isModalOpen"
        :legacyHeaderPadding="false"
        width="md:max-w-lg"
        @handle-close="isModalOpen = false"
      >
        <template #header>
          <Header4 font="gelica">Promotion Details</Header4>
        </template>
        <template #body>
          <Header5 class="mt-6 font-semibold">{{ promoAttached.cartDiscount.name.en }}</Header5>
          <BaseBodyText v-if="promoAttached.cartDiscount.description" class="mt-4">
            {{ promoAttached.cartDiscount.description.en }}
          </BaseBodyText>
          <RouteLink
            v-if="promoAttached.cartDiscount.custom?.fields.landingPagePath"
            :to="promoAttached.cartDiscount.custom.fields.landingPagePath"
            @click="isModalOpen = false"
          >
            <ThemedButton class="my-4" theme="gray" tabindex="-1">
              See Eligible Products
            </ThemedButton>
          </RouteLink>
        </template>
      </Modal>
    </Teleport>
  </div>
</template>
